import { useMemo } from "react";
import { Field, Step } from "@lib/shared/types";
import { FieldData } from "../types";
import { flatten } from "@lib/shared/lodash";

export type FieldWithValue = Partial<Field> & FieldData;

export interface UseInvalidFieldsArgs {
    steps: Step[];
    singleQuestion: boolean | undefined;
    singleQuestionMaxOptions: number | string | undefined;
    formErrors: unknown;
    fieldsData: { [k: string]: FieldData };
}

/**
 * Computes the array of fields that failed validation when `formErrors` is
 * truthy. Also performs the legacy in-place slicing of the first field's
 * options when `singleQuestion` is enabled (preserving existing behavior).
 */
export function useInvalidFields({
    steps,
    singleQuestion,
    singleQuestionMaxOptions,
    formErrors,
    fieldsData,
}: UseInvalidFieldsArgs): FieldWithValue[] {
    return useMemo(() => {
        const fields = flatten(steps.map((step) => step.fields));
        if (
            singleQuestion &&
            singleQuestionMaxOptions &&
            fields[0].fieldType == "select" &&
            fields[0].defaultValues?.options
        ) {
            fields[0].defaultValues.options =
                fields[0].defaultValues.options.slice(
                    0,
                    Number(singleQuestionMaxOptions) - 1,
                );

            //add older option
            fields[0].defaultValues.options.push({
                label: "Older",
                value: "",
                id: 1,
                icon: null,
            });
        }

        if (formErrors) {
            const allFields: string[] = Object.keys(fieldsData);

            const invalidFieldsArray: FieldWithValue[] = [];

            allFields.forEach((field) => {
                if (fieldsData[field].valid === false) {
                    invalidFieldsArray.push({
                        ...fieldsData[field],
                        ...(fields.find((f) => f.codeName === field) ?? {}),
                    } as FieldWithValue);
                }
            });

            return invalidFieldsArray;
        }
        return [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formErrors]);
}
